import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api, ILocation, IPlace } from '../api/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 16px;
    border-radius: 12px;
    gap: 16px;
    margin: 16px;
  }
  
  @media (max-width: 480px) {
    width: calc(100% - 32px);
    padding: 16px;
    margin: 16px;
    max-height: calc(100vh - 32px);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
`;

const Input = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #284640;
  background-color: #172A2A;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  &::placeholder {
    color: #838D90;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px; /* Предотвращает zoom на iOS */
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #284640;
  background-color: #172A2A;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  option {
    background-color: #172A2A;
    color: #FFFFFF;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px; /* Предотвращает zoom на iOS */
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const InputField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 16px;
    width: 100%;
  }
  
  ${props => props.variant === 'primary' ? `
    background-color: #24BA7D;
    color: #FFFFFF;
    
    &:hover {
      background-color: #1E9A66;
    }
    
    &:disabled {
      background-color: #284640;
      color: #838D90;
      cursor: not-allowed;
    }
  ` : `
    background-color: transparent;
    color: #838D90;
    border: 1px solid #284640;
    
    &:hover {
      background-color: #172A2A;
      color: #FFFFFF;
    }
  `}
`;

interface SetPlaceModalProps {
  isOpen: boolean;
  productCode?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const SetPlaceModal: React.FC<SetPlaceModalProps> = ({
  isOpen,
  productCode = '',
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    locationId: '',
    letter: '',
    number: ''
  });
  
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [places, setPlaces] = useState<IPlace[]>([]);
  const [loading, setLoading] = useState(false);

  // Загружаем локации при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      loadLocations();
    }
  }, [isOpen]);

  // Загружаем места при изменении локации
  useEffect(() => {
    if (formData.locationId) {
      loadPlaces(parseInt(formData.locationId));
    } else {
      setPlaces([]);
    }
  }, [formData.locationId]);

  const loadLocations = async () => {
    try {
      const data = await api.getLocations();
      setLocations(data);
    } catch (error) {
      console.error('Ошибка при загрузке локаций:', error);
    }
  };

  const loadPlaces = async (locationId: number) => {
    try {
      const data = await api.getPlaces(locationId);
      setPlaces(data);
    } catch (error) {
      console.error('Ошибка при загрузке мест:', error);
    }
  };

  // Поиск места по букве и номеру
  const findPlace = (): IPlace | null => {
    if (!formData.letter || !formData.number || !formData.locationId) {
      return null;
    }
    
    return places.find(place => 
      place.letter.toLowerCase() === formData.letter.toLowerCase() && 
      place.number === parseInt(formData.number)
    ) || null;
  };

  const handleSubmit = async () => {
    if (!productCode) {
      return;
    }

    // Ищем существующее место
    const foundPlace = findPlace();
    if (!foundPlace) {
      console.error('Место не найдено');
      return;
    }

    setLoading(true);
    try {
      await api.setPlace({
        product_code: productCode,
        place_id: foundPlace.id
      });
      
      console.log('Место успешно установлено');
      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Ошибка при установке места:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      locationId: '',
      letter: '',
      number: ''
    });
    setPlaces([]);
    onClose();
  };

  const isFormValid = () => {
    if (!productCode || !formData.locationId) {
      return false;
    }
    
    // Проверяем, что место существует
    const foundPlace = findPlace();
    return !!foundPlace;
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Установка места продукта</ModalTitle>
        </ModalHeader>

        <FormField>
          <Label>Локация</Label>
          <Select
            value={formData.locationId}
            onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
          >
            <option value="">Выберите локацию</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField>
          <Label>Место</Label>
          <InputRow>
            <InputField>
              <Label>Буква</Label>
              <Input
                type="text"
                placeholder="A"
                value={formData.letter}
                onChange={(e) => setFormData(prev => ({ ...prev, letter: e.target.value.toUpperCase() }))}
                maxLength={2}
                disabled={!formData.locationId}
              />
            </InputField>
            <InputField>
              <Label>Номер</Label>
              <Input
                type="number"
                placeholder="1"
                value={formData.number}
                onChange={(e) => setFormData(prev => ({ ...prev, number: e.target.value }))}
                min="1"
                disabled={!formData.locationId}
              />
            </InputField>
          </InputRow>
          {formData.letter && formData.number && formData.locationId && (
            <div style={{ fontSize: '12px', color: findPlace() ? '#24BA7D' : '#ff6b6b', marginTop: '4px' }}>
              {findPlace() ? 
                `✓ Место ${formData.letter}${formData.number} найдено` : 
                `✗ Место ${formData.letter}${formData.number} не найдено в выбранной локации`
              }
            </div>
          )}
        </FormField>

        <ButtonContainer>
          <Button variant="secondary" onClick={handleClose}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? 'Установка...' : 'Установить место'}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SetPlaceModal;
